/* eslint-disable no-nested-ternary */
/* eslint-disable no-underscore-dangle */
/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable max-len */
/* eslint-disable import/no-cycle */
import styled from '@emotion/styled';
import { define, s, ui } from '@owenscorning/pcb.alpha';
import { useEffect, useState } from 'react';

import { mdms } from '../../../../../api';
import useDeviceBreakpoint from '../../../../../hooks/use_device_breakpoint';
import useReference from '../../../../../hooks/use_reference';
import CMSContentAndImage from '../../../../OC/PageBuilder/ContentAndImage';
import { highlightSettingsSection } from '../../../../OC/PageBuilder/utilities/shared';
import { connectLocation } from '../../../../location/LocationConnectors';
import { Renderer as CardSet } from '../Page/CardSet';

const featureChoices = {
  trudefinition: 'Color Platform',
  warranty_25_year: 'Limited 25 Year Warranty',
  warranty_limited_lifetime: 'Limited Lifetime Warranty',
  premium_color_blends: 'Premium Color Blends',
  sbs: 'SBS Modified Asphalt',
  algae_resistance: 'StreakGuard',
  surenail: 'SureNail',
  weatherguard: 'WeatherGuard Technology',
  wind_resistance: 'Wind Resistance',
};

const getReferenceData = (name, language) => {
  const refObj = {
    __ref: `Cms::Content|{"type":"Reusable","path":"roofing","name":"${name}","language":"${language}"}`,
  };
  const data = useReference(refObj);
  const moduleData = data?.results?.contents?.sections?.[0]?.modules?.[0]?.data;
  return moduleData || {};
};

const getContentAndImageDataAsCard = ({ media = null, content = null }) => ({
  image: media?.resources?.image,
  prehead: content?.prehead,
  heading: content?.heading,
  text: content?.bodyCopy,
  cta: content?.ctas?.map(cta => ({ ...cta, variant: 'chevron' })),
  ctaSwitch: true,
});

const getAllData = localeCode => {
  const data = new Map();
  data.set('trudefinition', getReferenceData('Color Platform Feature Benefit', localeCode));
  data.set('warranty_limited_lifetime', getReferenceData('Limited Lifetime Warranty Feature Benefit', localeCode));
  data.set('warranty_25_year', getReferenceData('Limited 25 Year Warranty Feature Benefit', localeCode));
  data.set('wind_resistance', getReferenceData('Wind Resistance Feature Benefit', localeCode));
  data.set('weatherguard', getReferenceData('WeatherGuard Technology Feature Benefit', localeCode));
  data.set('premium_color_blends', getReferenceData('Premium Color Blends Feature Benefit', localeCode));
  data.set('algae_resistance', getReferenceData('StreakGuard Feature Benefit', localeCode));
  data.set('surenail', getReferenceData('SureNail Feature Benefit', localeCode));
  data.set('sbs', getReferenceData('SBS Modified Asphalt Feature Benefit', localeCode));
  return data;
};

// header overrides and ensuring full-width
const ContentAndImageContainer = styled.div`
  margin-bottom: 64px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  & .content h3 {
    font-size: 28px;
    line-height: 30px;
  }
  div > div {
    width: 100%;
  }
`;

const addCardOrDetermineIfFeatured = (data, benefitName, cardsData, featured) => {
  const cardData = data.get(benefitName);
  if (benefitName === featured) {
    return cardData;
  }
  cardsData.push(cardData);
  return null;
};

const buildCardsData = (attributes, data, featured, featureOrder, showPremiumColorBlends) => {
  // iterate through desired feature order while checking attributes + order visibility to determine what to show.
  // note a "featured" feature if it exists without adding it to the cards.
  const cardsData = [];
  const basicAttributeKeys = ['trudefinition', 'wind_resistance', 'weatherguard', 'surenail', 'sbs'];
  let featuredData = null;

  featureOrder?.forEach(f => {
    const [featureToken, isVisible] = Object.entries(f)[0];
    if (isVisible) {
      if (
        (basicAttributeKeys.includes(featureToken) && attributes?.[featureToken]) ||
        (featureToken === 'warranty_limited_lifetime' && attributes?.warranty === 'limited-lifetime') ||
        (featureToken === 'warranty_25_year' && attributes?.warranty === '25-year') ||
        (featureToken === 'algae_resistance' && attributes?.algae_resistance?.length) ||
        (featureToken === 'premium_color_blends' && showPremiumColorBlends)
      ) {
        featuredData = addCardOrDetermineIfFeatured(data, featureToken, cardsData, featured) ?? featuredData;
      }
    }
  });

  return [cardsData, featuredData];
};

const isDataReady = data => {
  const readyCount = Array.from(data.values()).filter(v => Object.keys(v).length > 0).length;
  return readyCount === data.size;
};

const ShingleFeaturesAndBenefits = connectLocation(({ attributes, featured, featureOrder, locale: { code }, zip }) => {
  const [showPremiumColorBlends, setShowPremiumColorBlends] = useState(false);
  const data = getAllData(code);
  const { isPhone, isTablet } = useDeviceBreakpoint();

  useEffect(() => {
    const pageSlug = Board?.build?.slug;
    mdms
      .get(`/api/v2/product/shingles/${pageSlug}?zip=${zip}&lang=${code}`)
      .then(response => response.json())
      .then(responseJson => {
        setShowPremiumColorBlends(responseJson?.shingle_colors?.find(color => color?.premium_color_blend));
      });
  }, []);

  if (!isDataReady(data)) {
    return null;
  }

  const [cardsData, featuredData] = buildCardsData(attributes, data, featured, featureOrder, showPremiumColorBlends);

  const cardsPerRow = isPhone ? 1 : isTablet ? 2 : Math.min(cardsData.length, 4);

  const cardSetSettings = {
    itemsPerRow: cardsPerRow,
    cardSize: 'md',
    aspectRatio: { selection: 'custom', custom: cardsPerRow === 2 && !isPhone ? 1.77 : 1.44 },
  };

  return (
    <>
      <ContentAndImageContainer>
        {featuredData && <CMSContentAndImage {...featuredData} />}
        {!isPhone && cardsData.length === 1 && (
          <CMSContentAndImage {...cardsData[0]} arrangement={{ ...cardsData[0].arrangement, contentOrder: 'last' }} />
        )}
      </ContentAndImageContainer>

      {(isPhone || cardsData.length > 1) && (
        <CardSet
          cards={cardsData.map(c => getContentAndImageDataAsCard(c))}
          settings={cardSetSettings}
          carousel={{ enabled: true }}
        />
      )}
    </>
  );
});

export default define`Shingle Features and Benefits`('0.0.1')({
  thumbnail: ui`Modules/Page/Thumbnails/Modules/Page/Thumbnails/Placeholder`,
  view: ({ Board, value }) => (
    <ShingleFeaturesAndBenefits
      attributes={Board?.settings?.attributes}
      featured={value?.featured}
      featureOrder={value?.featureOrder?.orderedList}
    />
  ),
  edit: ui`Form`.of({
    [s._]: ui`Tip`.of(
      'The features displayed on the page are mostly determined by Attributes with the exception of Premium Color Blends, which appears when present on any Shingle Colors.'
    ),
    [s._]: ui`RawLink`.of('Go to settings to change attributes')({
      onClick: () => {
        highlightSettingsSection('sidebar-pane', 'attributes');
      },
      noTopMargin: true,
      icon: 'external-link',
    }),
    featured: ui`Choices`.of({
      ...featureChoices,
    })({
      label: 'Top Feature',
      default: 'trudefinition',
      mode: ui`Choices/Mode/Dropdown`,
    }),
    [s._]: ui`Tip`.of(
      'Drag below to change the order of the remaining cards. Note that below is a list of ALL potential features, not just the ones currently displayed on the page.'
    ),
    featureOrder: ui`ChoicesDraggable`.of(featureChoices)({
      label: 'Remaining Features Order',
      default: {
        enabledFilters: featureChoices,
        orderedList: Object.keys(featureChoices).map(k => ({ [k]: true }))
      }
    }),
  })({
    label: 'Features and Benefits',
  }),
});
